import React, { useRef, useCallback, useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  AttachFile as AttachIcon,
  PlayCircleOutline as PlayIcon,
  ZoomIn as ZoomInIcon,
} from '@mui/icons-material';

/**
 * Reusable file picker for a single media file.
 * Shows a compact preview thumbnail for images/videos.
 * Click the preview to open a full-size lightbox viewer.
 */
const PlatformMediaPicker = React.memo(({
  value,         // File | null
  onChange,      // (File | null) => void
  accept = 'image/*,video/*',
  label = 'Media file (optional)',
  compact = false,  // smaller variant for inside accordions
  error = null      // validation error message
}) => {
  const fileInputRef = useRef(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const handleChange = useCallback((e) => {
    onChange(e.target.files[0] || null);
  }, [onChange]);

  const handleClear = useCallback(() => {
    onChange(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [onChange]);

  // Create an object URL for the preview — revoked on cleanup / file change
  const previewUrl = useMemo(() => {
    if (!value) return null;
    return URL.createObjectURL(value);
  }, [value]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const isImage = value?.type?.startsWith('image/');
  const isVideo = value?.type?.startsWith('video/');

  const thumbSize = compact ? 64 : 80;

  return (
    <Box>
      {label && (
        <Typography
          variant={compact ? 'caption' : 'body2'}
          sx={{ fontWeight: 500, mb: 0.75, color: 'text.secondary', display: 'block' }}
        >
          {label}
        </Typography>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={handleChange}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size={compact ? 'small' : 'medium'}
          startIcon={value ? <AttachIcon /> : <CloudUploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          sx={{ borderRadius: 2, flexShrink: 0 }}
        >
          {value ? 'Change' : 'Choose File'}
        </Button>

        {value && (
          <>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {value.name}
            </Typography>
            <IconButton size="small" onClick={handleClear} title="Remove file">
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>

      {error && (
        <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block', fontWeight: 600 }}>
          {typeof error === 'string' ? error : (error?.message || (error ? String(error) : ''))}
        </Typography>
      )}

      {value && (
        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
          {(value.size / (1024 * 1024)).toFixed(1)} MB · {value.type || 'unknown type'}
        </Typography>
      )}

      {/* ── Compact preview thumbnail ── */}
      {previewUrl && (isImage || isVideo) && (
        <Box
          onClick={() => setLightboxOpen(true)}
          sx={{
            mt: 1.5,
            position: 'relative',
            width: thumbSize,
            height: thumbSize,
            borderRadius: 1.5,
            overflow: 'hidden',
            cursor: 'pointer',
            border: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
            '&:hover .preview-overlay': { opacity: 1 },
          }}
        >
          {isImage ? (
            <Box
              component="img"
              src={previewUrl}
              alt="preview"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Box
              component="video"
              src={previewUrl}
              muted
              preload="metadata"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          )}

          {/* Hover overlay */}
          <Box
            className="preview-overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 0.2s',
            }}
          >
            {isVideo ? (
              <PlayIcon sx={{ color: 'white', fontSize: 28 }} />
            ) : (
              <ZoomInIcon sx={{ color: 'white', fontSize: 24 }} />
            )}
          </Box>
        </Box>
      )}

      {/* ── Full-size lightbox ── */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            borderRadius: 3,
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 2.5,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mr: 2,
            }}
          >
            {value?.name}
          </Typography>
          <IconButton size="small" onClick={() => setLightboxOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#0a0a0a',
          }}
        >
          {previewUrl && isImage && (
            <Box
              component="img"
              src={previewUrl}
              alt="full preview"
              sx={{
                maxWidth: '85vw',
                maxHeight: '80vh',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          )}
          {previewUrl && isVideo && (
            <Box
              component="video"
              src={previewUrl}
              controls
              autoPlay
              sx={{
                maxWidth: '85vw',
                maxHeight: '80vh',
                objectFit: 'contain',
                display: 'block',
                outline: 'none',
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
});

export default PlatformMediaPicker;
